import React, { Fragment, useEffect, useState } from 'react';
import Humanize from 'humanize-plus';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled, { css } from 'styled-components';

import { renderFormField } from 'modules/form';
import { studentUpgradeFields, HYBRID_ENROLLMENT_TYPES } from 'constants/form';
import { Button, Row, Separator, Text, Col } from '@cuemath/comps';
import BallLoader from 'comps/ball-loader';
import { Message } from 'modules/form';
import COLORS from 'constants/colors';
import Tooltip from 'comps/tooltip';

import {
  post as upgradeStudentCourse,
  emptyStoreData,
} from 'actions/student-course-upgrade';
import { get as getOnlinePlans } from 'actions/pricing';
import { closeModal } from 'actions/modal';
import { CURRENCY_SYMBOL_MAP } from 'modals/utils/currency-helper';
import crossCircle from 'img/black-cross-icon.svg';
import copyToClipboard from 'img/copy_to_clipboard.svg';
import { COURSE_CODE } from '../../constants/course-code';

const Wrapper = styled.div`
  padding: 40px 10px 20px;
`;

const ButtonWrapper = styled(Row)`
  justify-content: center;
`;

const RowWrapper = styled(Row)`
  justify-content: center;
  margin: 5px 0px;
`;

const PromoCode = styled(Row)`
  border-radius: 4px;
  border: 1px solid ${COLORS.DISABLE_COLOR};
  padding: 4px;
  gap: 10px;
  width: max-content;
  margin-left: 35%;
`;

const PromoCodeInput = styled.input`
  border: none;
  font-size: 16px;
  &:focus {
    outline: none;
  }
`;

const TextWrapper = styled.label`
  cursor: pointer;
  margin-left: 58%;
  &:hover {
    border-bottom: none;
  }
  padding: 2px;
  border-bottom: 1px solid ${COLORS.LINK};
  color: ${COLORS.LINK};
`;

const LinkDiv = styled.div`
  background-color: ${COLORS.READONLY};
  border: 1px solid ${COLORS.DISABLE_COLOR};
  border-radius: 4px;
  padding: 5px;
  ${({ icon }) =>
    icon &&
    css`
      cursor: pointer;
    `}
`;

const CouponMessage = styled(Message)`
  margin-left: ${({ leftSpace }) => leftSpace && '35%'};
  margin-top: ${({ leftSpace }) => leftSpace && '2px'};
`;

const RowWithGap = styled(Row)`
  gap: ${({ gap }) => gap}px;
`;

const COPY_STATE = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
};

let timeout = null;

const StudentUpgradeForm = ({
  studentCourseUpgrade,
  pricing,
  studentCourse,
  grade,
  actions,
}) => {
  const [form, setForm] = useState(getFormDefaults());
  const [fields, setFields] = useState(studentUpgradeFields);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [success, setSuccess] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [isPromoChange, setIsPromoChange] = useState(false);
  const [contentCopyState, setContentCopyState] = useState(null);

  const { id: studentCourseId, course_code: courseCode } = studentCourse || {};
  const IndiaRegion = courseCode === COURSE_CODE.INDIA_MATH;

  const studentCourseUpgradePostData = store.post(studentCourseUpgrade, studentCourseId);
  const { resolved: postDataResolved } = studentCourseUpgradePostData || {};
  const errorObj = postDataResolved && store.getError(postDataResolved);
  const { message } = errorObj || {};
  const { data: postData, isCreated, isCreatingFailed } = postDataResolved || {};
  const {
    balance_amount: balanceAmount,
    discount_summary: discountSummary,
    fee_adjustment_summary: {
      amount: adjustmentAmount,
      desc: adjustmentDescription,
    } = {},
    eligible_discounts: eligibleDiscounts,
    credits,
    upgrade_successful,
    link: upgradeLink,
    transition_state: transitionState,
    currency,
  } = postData || {};
  const { applied_credits: appliedCredits } = credits || {};
  const { discounts: paymentDiscounts = [], invalid_discounts: invalidDiscounts = [] } =
    discountSummary || {};

  const plansData = store.get(pricing, form.online_band_list, {
    grade,
    is_upgrade: true,
    enrollment_type: form.enrollment_type,
    is_active: true,
  });
  const getErrorObj = plansData && store.getError(plansData);
  const { message: getErrorMsg } = getErrorObj || {};
  const { data: onlinePlans, isLoaded, isLoadingFailed } = plansData || {};

  const fillForm = data => {
    if (!data) return;
    let newForm = {};
    const { discount_amount: discountAmount, pending_classes: pendingClasses } =
      data || {};

    if (discountAmount !== undefined && pendingClasses !== undefined) {
      newForm.discount_amount = discountAmount;
      newForm.pending_classes = pendingClasses.toString();
    }

    setForm({ ...form, ...newForm });
  };

  const fillFields = data => {
    if (data && Array.isArray(data) && !data.length) {
      setError('No Plan found');
      setLoading(false);
    }
    if (!data) return;
    const { config, can_upgrade: canUpgrade, online_band_list: onlineBandList } =
      data || {};
    const { allowed_enrollment_type: allowedEnrollmentType } = config || {};

    if (transitionState === 'CONFIRMED') return;

    const newFields =
      fields &&
      fields.map(field => {
        if (field.name === 'enrollment_type' && allowedEnrollmentType) {
          const modifiedChoices = allowedEnrollmentType.map(choice => {
            const label = choice;
            const value = choice;
            return { label, value };
          });
          return { ...field, choices: modifiedChoices, disabled: false };
        }
        if (
          field.name === 'online_band_list' &&
          canUpgrade &&
          Array.isArray(onlineBandList) &&
          onlineBandList.length
        ) {
          const modifiedChoices = onlineBandList.map(choice => {
            const label = choice.name;
            const value = choice.id;
            return { label, value };
          });
          return { ...field, choices: modifiedChoices, disabled: false };
        }
        if (
          field.name === 'online_plan' &&
          Array.isArray(onlinePlans) &&
          onlinePlans.length
        ) {
          const modifiedChoices = onlinePlans.map(choice => {
            const label = choice.name;
            const value = choice.id;
            return { label, value };
          });
          return { ...field, choices: modifiedChoices, disabled: false };
        }
        return field;
      });

    setFields(newFields);
  };

  const onCloseModal = () => {
    setTimeout(() => {
      actions.closeModal();
    }, 1500);
  };

  const disableAllFields = () => {
    const newFields = fields && fields.map(field => ({ ...field, disabled: true }));
    setFields(newFields);
    if (!IndiaRegion) onCloseModal();
  };

  useEffect(() => {
    if (studentCourseId) {
      const postData = {
        action: 'upgrade_details',
      };
      actions.upgradeStudentCourse(studentCourseId, postData);

      setLoading(true);
    }
    return () => {
      actions.emptyStoreData({ items: {} });
    };
  }, []);

  useEffect(() => {
    if (studentCourseId) {
      const isPaymentSummaryAvailable = getPaymentSummary();
      const planAmount = getPlanAmount();
      const netPayableAmount = balanceAmount ? balanceAmount : 'N.A.';
      if (isCreated) {
        if (isPaymentSummaryAvailable) {
          setPaymentSummary({
            planAmount,
            netPayableAmount,
            discounts: paymentDiscounts,
            invalidDiscounts,
            eligibleDiscounts,
            feeAdjustmentAmount: adjustmentAmount,
            feeAdjustmentDescription: adjustmentDescription,
            creditsApplied: appliedCredits,
            feeCurrency: currency,
          });
        } else if (upgrade_successful) {
          disableAllFields();
          setSuccess(true);
        } else {
          fillForm(postData);
          fillFields(postData);
        }
        setLoading(false);
      } else if (isCreatingFailed) {
        if (message) {
          setError(message);
        } else {
          setError('Something went wrong!');
        }
        setLoading(false);
      }
    }
  }, [isCreated, isCreatingFailed]);

  useEffect(() => {
    if (studentCourseId) {
      if (onlinePlans && isLoaded) {
        fillFields(onlinePlans);
        setLoading(false);
      } else if (getErrorMsg && isLoadingFailed) {
        setError(getErrorMsg);
        setLoading(false);
      }
    }
  }, [isLoaded, isLoadingFailed]);

  useEffect(() => {
    if (contentCopyState) {
      timeout = setTimeout(() => {
        setContentCopyState(null);
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [contentCopyState]);

  function getFormDefaults() {
    const formDefaults = {};

    studentUpgradeFields.forEach(field => {
      formDefaults[field.name] = field.default;
    });
    return formDefaults;
  }

  const resetFields = fieldsToReset => {
    const newFields =
      fields &&
      fields.map(field => {
        if (fieldsToReset[field.name]) {
          return { ...field, choices: [], disabled: true };
        }
        return field;
      });
    setFields(newFields);
  };

  const resetForm = formToReset => {
    const newForm = form;
    for (let key in newForm) {
      if (formToReset[key]) {
        newForm[key] = '';
      }
    }
    setForm(newForm);
  };

  const checkOtherSelections = key => {
    if (key === 'enrollment_type') {
      resetFields({ online_band_list: true, online_plan: true });
      resetForm({ online_band_list: true, online_plan: true });
    } else if (key === 'online_band_list') {
      resetFields({ online_plan: true });
      resetForm({ online_band_list: true, online_plan: true });
    }
  };

  const onOptionChange = (key, value) => {
    let error;
    let loading = true;
    let valueToUpdate = value;
    if (key === 'enrollment_type') {
      const postData = {
        action: 'can_upgrade',
        action_data: {
          enrollment_type: value,
        },
      };
      checkOtherSelections(key);
      actions.upgradeStudentCourse(studentCourseId, postData);
    }

    if (key === 'online_band_list') {
      const { teacher } = studentCourse;
      const { enrollment_type: selectedEnrollmentType } = form;
      const { online_band_details: { online_band_id: teacherOnlineBandId } = {} } =
        teacher || {};
      const isHybridPlan = HYBRID_ENROLLMENT_TYPES.includes(selectedEnrollmentType);
      const isBandMismatch = isHybridPlan && value && value !== teacherOnlineBandId;
      if (isBandMismatch) {
        error = "Selected plan's band does not match with teacher's online band";
        loading = false;
        valueToUpdate = null;
      }
      checkOtherSelections(key);
      if (!error) {
        const query = {
          grade,
          is_upgrade: true,
          enrollment_type: form.enrollment_type,
          is_active: true,
        };
        actions.getOnlinePlans(value, query);
      }
    }

    if (key === 'online_plan') {
      const postData = {
        action: 'payment_summary',
        action_data: {
          online_plan_id: value,
        },
      };
      actions.upgradeStudentCourse(studentCourseId, postData);
    }

    const newForm = { ...form };

    newForm[key] = valueToUpdate;

    setForm(newForm);
    setLoading(loading);
    setError(error);
    setPaymentSummary(null);
    setPromoCode('');
    setShowPromoInput(false);
  };

  const onConfirm = () => {
    const { eligibleDiscounts = {} } = paymentSummary || {};
    const isPromoCodeApplied =
      Array.isArray(discounts) &&
      discounts.some(el => el.type === 'COUPON_CODE') &&
      eligibleDiscounts.COUPON_CODE === promoCode;

    const actionData = isPromoCodeApplied
      ? { online_plan_id: form.online_plan, coupon_code: promoCode }
      : {
          online_plan_id: form.online_plan,
        };

    const postData = {
      action: 'initiate_upgrade_process',
      action_data: actionData,
    };
    setLoading(true);
    actions.upgradeStudentCourse(studentCourseId, postData);
  };

  const checkButtonStatus = () => {
    if (!form.enrollment_type || !form.online_band_list || !form.online_plan) return true;
    return false;
  };

  function getPaymentSummary() {
    if (balanceAmount !== null && balanceAmount !== undefined) return true;
    return false;
  }

  function getPlanAmount() {
    if (!onlinePlans || !Array.isArray(onlinePlans) || !form.online_plan) return 'N.A.';
    return onlinePlans.find(plan => plan.id === form.online_plan).amount;
  }

  const getFormStatus = () => {
    for (let key in form) {
      if (form[key] === '') {
        return false;
      }
    }
    return true;
  };

  const renderTotalPayableAmount = (amountValue, separate) => {
    const { feeCurrency } = paymentSummary;
    return (
      <>
        {separate ? ' - ' : ''}
        {CURRENCY_SYMBOL_MAP[feeCurrency]}
        {Humanize.intComma(amountValue)}
      </>
    );
  };

  const renderSummaryWrapper = (label, value) => {
    return (
      <RowWrapper>
        <Col xs={6}>
          <Text>{label}</Text>
        </Col>
        <Col xs={6}>
          <Text align="right">{value}</Text>
        </Col>
      </RowWrapper>
    );
  };

  const applyPromo = () => {
    const { online_plan: onlinePLanId } = form || {};
    const postData = {
      action: 'payment_summary',
      action_data: {
        online_plan_id: onlinePLanId,
        coupon_code: promoCode,
      },
    };
    actions.upgradeStudentCourse(studentCourseId, postData);
    setLoading(true);
    setIsPromoChange(true);
  };

  const getPromoCodeStatus = () => {
    const { invalidDiscounts, discounts } = paymentSummary || {};
    const invalidCouponCodeApplied =
      Array.isArray(invalidDiscounts) &&
      invalidDiscounts.some(el => el.type === 'COUPON_CODE');
    const validCouponCodeApplied =
      Array.isArray(discounts) && discounts.some(el => el.type === 'COUPON_CODE');

    return { invalidCouponCodeApplied, validCouponCodeApplied };
  };

  const removeAppliedCoupon = () => {
    const { online_plan: onlinePLanId } = form || {};
    const postData = {
      action: 'payment_summary',
      action_data: {
        online_plan_id: onlinePLanId,
      },
    };
    actions.upgradeStudentCourse(studentCourseId, postData);
    setPromoCode('');
  };

  const emptyInvalidDiscounts = () => {
    const { invalidDiscounts } = paymentSummary || {};
    const filterInvalidCouponCodes =
      Array.isArray(invalidDiscounts) &&
      invalidDiscounts.filter(el => el.type !== 'COUPON_CODE');
    setPaymentSummary({ ...paymentSummary, invalidDiscounts: filterInvalidCouponCodes });
  };

  const handlePromoCode = e => {
    const { invalidDiscounts } = paymentSummary || {};
    const invalidCouponCodeApplied =
      Array.isArray(invalidDiscounts) &&
      invalidDiscounts.some(el => el.type === 'COUPON_CODE');

    setPromoCode(e.target.value);

    if (isPromoChange && invalidCouponCodeApplied) {
      emptyInvalidDiscounts();
      setIsPromoChange(false);
    }
  };

  const handleCopy = () => {
    const contentDiv = document.getElementById('content-to-copy');
    let content;
    if (contentDiv) {
      content = contentDiv.textContent;
      navigator.clipboard.writeText(content).then(
        () => {
          /* clipboard successfully set */
          setContentCopyState(COPY_STATE.SUCCESS);
        },
        () => {
          /* clipboard write failed */
          setContentCopyState(COPY_STATE.FAILED);
        },
      );
    }
  };

  /**
   * In case user contact details like mobile number and email id is not verified.
   */

  // if (!contactVerified) {
  //   return (
  //     <>
  //       <Separator height={20} transparent />
  //       <RowWrapper>
  //         <Message width="90%" error>
  //           This student will not receive upgrade confirmation link as the email or phone
  //           number is not verified. So please update both before proceeding.
  //         </Message>
  //       </RowWrapper>
  //     </>
  //   );
  // }

  const isButtonDisabled = checkButtonStatus();
  const completeFilled = getFormStatus();
  const buttonLabel =
    transitionState === 'CONFIRMED'
      ? 'Upgrade confirmed. Waiting for payment.'
      : 'Submit';

  const {
    planAmount,
    netPayableAmount,
    feeAdjustmentAmount,
    feeAdjustmentDescription,
    creditsApplied,
    discounts,
  } = paymentSummary || {};
  const showAppliedCredits = creditsApplied > 0;
  const showFeeAdjustments = feeAdjustmentAmount > 0;
  const showDiscount = Array.isArray(discounts);
  const { invalidCouponCodeApplied, validCouponCodeApplied } = getPromoCodeStatus();
  const linkCopied = contentCopyState === COPY_STATE.SUCCESS;
  const toolTipText = linkCopied ? 'Copied!' : 'Copy to clipboard';

  return (
    <Wrapper>
      {fields &&
        fields.map(field =>
          renderFormField({
            ...field,
            value: form[field.name],
            onChange: onOptionChange,
          }),
        )}
      {paymentSummary &&
        completeFilled &&
        !success &&
        (showPromoInput ? (
          <>
            <PromoCode justifyContent="space-between">
              <PromoCodeInput
                value={promoCode}
                onChange={handlePromoCode}
                disabled={validCouponCodeApplied}
              />
              {validCouponCodeApplied && promoCode && (
                <img
                  src={crossCircle}
                  onClick={removeAppliedCoupon}
                  width="32"
                  height="32"
                  alt="closed-icon"
                  style={{ cursor: 'pointer' }}
                />
              )}
              <Button
                align="center"
                compact
                label="Apply"
                disabled={!promoCode || validCouponCodeApplied || loading}
                onClick={applyPromo}
              />
            </PromoCode>
            {validCouponCodeApplied && promoCode && (
              <CouponMessage leftSpace width="max-content">
                Coupon Applied!
              </CouponMessage>
            )}
            {invalidCouponCodeApplied && promoCode && (
              <CouponMessage width="max-content" error leftSpace>
                Invalid Coupon Applied!
              </CouponMessage>
            )}
          </>
        ) : (
          <TextWrapper onClick={() => setShowPromoInput(true)}>
            Have a promo code?
          </TextWrapper>
        ))}
      {error && (
        <Message width="100%" error>
          {error}
        </Message>
      )}
      {paymentSummary && completeFilled && (
        <>
          <h3>Payment Summary</h3>
          {renderSummaryWrapper('Plan Amount', renderTotalPayableAmount(planAmount))}
          {showDiscount &&
            discounts.map(({ desc, amount }) => {
              return (
                <Fragment key={desc}>
                  {renderSummaryWrapper(desc, renderTotalPayableAmount(amount, true))}
                </Fragment>
              );
            })}
          {showAppliedCredits &&
            renderSummaryWrapper(
              'Credits Applied',
              renderTotalPayableAmount(creditsApplied, true),
            )}
          {showFeeAdjustments &&
            renderSummaryWrapper(
              feeAdjustmentDescription,
              renderTotalPayableAmount(feeAdjustmentAmount, true),
            )}
          <Separator height={2} />
          {renderSummaryWrapper(
            'Net Payable Amount',
            renderTotalPayableAmount(netPayableAmount),
          )}
        </>
      )}
      {success ? (
        <RowWithGap justifyContent="center" gap={5}>
          <LinkDiv id="content-to-copy">{upgradeLink}</LinkDiv>
          <Tooltip title={toolTipText} position="bottom" width="100px">
            <LinkDiv icon onClick={handleCopy}>
              <img src={copyToClipboard} width="20" height="20" alt="copy-to-clipbaord" />
            </LinkDiv>
          </Tooltip>
        </RowWithGap>
      ) : (
        <>
          <Separator transparent height="20" />
          <ButtonWrapper>
            {loading ? (
              <BallLoader />
            ) : (
              <Button
                align="center"
                label={buttonLabel}
                onClick={onConfirm}
                disabled={isButtonDisabled}
              />
            )}
          </ButtonWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default connect(
  ({ studentCourseUpgrade, pricing }) => ({ studentCourseUpgrade, pricing }),
  dispatch => ({
    actions: bindActionCreators(
      {
        upgradeStudentCourse,
        getOnlinePlans,
        emptyStoreData,
        closeModal,
      },
      dispatch,
    ),
  }),
)(StudentUpgradeForm);
